<template>
  <PageLoader v-if="loading"/>
  <div class="card">
    <div class="card-header flex-wrap pt-6 pb-0">
      <div class="card-title">
        <div class="card-label">
          <h3 class="card-label">{{ $t('projects.step_list') }}</h3>
        </div>
      </div>
      <div class="card-toolbar">
        <Button type="new" btn-class="btn-sm" @click="openProjectStepModal" btn-text="projects.create_step"></Button> 
      </div>
    </div>
    <div class="card-body">
      <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4" id="kt_steps">
        <thead>
          <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
            <th class="min-w-100px text-start">{{ $t('projects.name') }}</th>
            <th class="min-w-100px text-center">{{ $t('projects.all_tasks') }}</th>
            <th class="min-w-100px text-center">{{ $t('projects.tasks_in_progress') }}</th>
            <th class="min-w-100px text-center">{{ $t('projects.tasks_done') }} </th>
            <th class="min-w-100px text-center">{{ $t('projects.table.task_review') }} </th>
            <th class="min-w-100px text-center">{{ $t('projects.color') }} </th>
            <th class="min-w-100px text-center">{{ $t('projects.actions') }}</th>
          </tr>
        </thead>
        <Sortable id="container" @change="handleChangeOrder">
          <tr class="draggable" v-for="step in steps" :key="step?.id">
            <td class="draggable-handle text-start">{{step?.title}}</td>
            <td class="draggable-handle text-center">{{step.tasks_count}}</td>
            <td class="draggable-handle text-center">{{step.waiting_tasks}}</td>
            <td class="draggable-handle text-center">{{step.end_tasks_count}}</td>
            <td class="draggable-handle">
              <div class="d-flex justify-content-center align-items-center">
                  <div class="d-flex align-items-center me-5" v-if="step?.total_task_remaining_review">
                      <span class="me-2" style="color: #5e6278;">{{ (step?.total_task_remaining_review || 0) + '/' + (step?.total_task_need_review || 0) }}</span>
                      <img src="/assets/media/tasks/review.png" style="height: 20px;" :title="$t('projects.label.total_task_remaining_review')"/>
                  </div>
                  <div class="d-flex align-items-center" v-if="step?.total_task_reviewed">
                      <span class="me-2 text-success">{{ (step?.total_task_reviewed || 0) + '/' + (step?.total_task_need_review || 0) }}</span>
                      <img src="/assets/media/tasks/validate.png" style="height: 20px;" :title="$t('projects.label.total_task_reviewed')" />
                  </div>
              </div>
            </td>
            <td class="draggable-handle d-flex justify-content-center">
              <div 
                :style="{
                  backgroundColor: step.color, 
                  borderRadius: '10px', 
                  height: '8px', 
                  width: '50%'
                }"
              ></div>
            </td>
            <td class="draggable-handle text-center" style="cursor: pointer !important;">
              <i v-if="$can(abilities.STEP_PROJECT_UPDATE)" class="fas fa-edit fs-2 me-5" style="color: #989EA7; cursor: pointer !important;" @click="editStepModal(step)"></i>
              <i v-if="$can(abilities.STEP_PROJECT_DELETE)" class="fas fa-trash fs-2" style="color: #989EA7; cursor: pointer !important;" @click="deleteStep(step?.id)"></i>
            </td>
          </tr>

        </Sortable>
        <tbody v-if="steps.length == 0 && !loading">
          <tr>
            <td colspan="6" class="text-center">
              <Card class="card p-5 h-full">
                <div class=" text-center ">
                  <div class="pt-8 ">
                    <Img filename="empty.png" alt=""
                         class="mw-100 h-125px h-sm-175px" />
                  </div>
                  <h2 class="fs-2x fw-bolder mb-0">{{ $t('projects.noStep') }}</h2>
                  <br>
                </div>
              </Card>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <UpdateStepModal id="step_modal_update" :form="formData" @load="reload"/>
  <StepSelectionForm id="step_modal_create" @save="handleStepSave" :title="$t('steps.create_step')" :loading="isLoading" :selectedSteps="selectedStepsIds"/>
</template>

<script setup> 
  import {onMounted, ref, computed} from "vue";
  import {useStore} from "vuex";
  import Button from "@/components/Button";
  import {useRoute} from 'vue-router';
  import Card from "@/components/Card"
  import {useSwal} from "@/composables/useSwal";
  import { useI18n } from 'vue-i18n'
  import UpdateStepModal from "./modals/UpdateStepModal.vue"
  import PageLoader from "@/components/PageLoader.vue";
  import abilities from "@/composables/abilities";
  import Img from "@/components/Img.vue";
  import Sortable from "@/components/Sortable.vue";
  import StepSelectionForm from "@/Modules/Projects/components/ProjectType/StepSelectionForm.vue";

  const store = useStore()
  const route = useRoute()
  const {showSwal} = useSwal()
  const { t } = useI18n({ useScope: 'global' })

  const steps = ref([])
  const loading = ref(false)
  const isLoading = ref(false)

  const currentSociety = computed(() => store.getters['offices/getSelectedCompany'])
  const selectedStepsIds = computed(() => steps.value?.map(el => el.milestone_category_id) || [])

  const formData = ref({
    id: "",
    title: "",
    milestone_category_id: "",
    color: "",
    society_id: currentSociety?.value?.id,
    status: [],
    milestone_categories_progress: 0,
    default: false,
    project_id: route.params.id,
    delay_value: '',
    delay_unit: ''
  })

  function openProjectStepModal(){
    formData.value.milestone_category_id = "",
    formData.value.title = "",
    formData.value.color = "#000",
    formData.value.society_id = currentSociety?.value?.id
    formData.value.status = [],
    formData.value.milestone_categories_progress = 0,
    formData.value.default = false
    formData.value.project_id = route.params.id
    formData.value.delay_value = ''
    formData.value.delay_unit= ''
    $('#step_modal_create').modal('toggle')
  }

  function editStepModal(step){
    let ids = step?.task_statuses?.map(item => item.task_status_id) || [];

    formData.value = {
      id: step?.id,
      title: step?.title,
      milestone_category_id: step?.milestone_category_id,
      society_id:  currentSociety?.value?.id,
      color: step?.color,
      status: ids,
      milestone_categories_progress: parseInt(step?.milestone_categories_progress),
      default: step?.default,
      project_id: route.params.id,
      delay_value: step?.delay_value,
      delay_unit: step?.delay_unit
    }

    $('#step_modal_update').modal('toggle')
  }

  function handleStepSave(data) {
    let formStep = {
      id: "",
      title: data.milestone_category_name,
      milestone_category_id: data.milestone_category_id,
      color: "#000",
      society_id: currentSociety?.value?.id,
      status: data.status,
      milestone_categories_progress: data.milestone_categories_progress,
      default: false,
      project_id: route.params.id,
      delay_value: data.delay_value,
      delay_unit: data.delay_unit
    }
    isLoading.value = true;
    store
        .dispatch("projectSteps/createProjectStep", formStep)
        .then((resp) => {
          reload()
          isLoading.value = false;
          window.toastr["success"](i18n.global.t("notification.step_created"));
        })
        .catch((e) => {
          isLoading.value = false;
        });
  }

  async function deleteStep(id){
    showSwal(
        t('swal.step'),
        t('swal.step_text')
    ).then( async (result) => {
      if (result.value) { 
        try {
          await store.dispatch('projectSteps/deleteProjectStep', id);
          loadProjectStepsData()
          loadStep()
          window.toastr["success"](i18n.global.t("notification.step_deleted"));
        } catch (error) {
          console.error("eeee", error); 
        } 
      }
    });
  }

  function loadStep(){
    store.dispatch('projectSteps/loadProjectSteps', route.params.id)
  }

  async function loadProjectStepsData() {
    loading.value = true;
    
    try {
      const res = await store.dispatch('projectSteps/loadProjectStepsWithTaskCount', route.params.id);
      steps.value = res.data?.data;

      if (steps.value && Object.values(steps.value).length > 0) {
      }
    } catch (error) {
      console.error("eeee", error); 
    } finally {
      loading.value = false; 
    }
  }

  function handleChangeOrder({ oldIndex, newIndex }) {
    if (oldIndex === newIndex) return;
    const itemToMove = steps.value[oldIndex];
    steps.value.splice(oldIndex, 1);
    steps.value.splice(newIndex, 0, itemToMove);
    steps.value.forEach((item, index) => {
      item.position = index + 1;
    });
    store.dispatch('projectSteps/changePositionStep', {id: itemToMove.id, position: newIndex + 1})
  }

  function reload(){
    loadProjectStepsData()
    loadStep()
  }

  onMounted(() => {
    loadProjectStepsData()
  })
</script>